// The one place a list route normalizes its query-string list state (ADR 059 —
// Search Spine § 2.7 term handling, § 2.9 "malformed → default"). List state
// lives in the URL, so a stale or hand-edited link reaches the route verbatim.

import {
  LIST_DATE_PRESETS,
  parseLogicalDateRange,
  type ListDatePreset,
  type LogicalDateRange,
} from './date-preset-logical-range';

/**
 * Longest search term a list route hands its repository.
 *
 * Not a validation rule — a term this long cannot match anything anyway. It
 * bounds the pattern the repository builds so a pathological input stays a
 * bounded one; over-length input is TRUNCATED rather than rejected, because a
 * search box that refuses a paste is worse than one that searches the first
 * 100 characters of it. (BL-1181's Lot Order precedent, generalized.)
 */
export const MAX_LIST_SEARCH_LENGTH = 100;

/**
 * Highest page a list route will read. Beyond it the page is malformed, not
 * late: `(page - 1) * pageSize` must stay inside Postgres's `bigint` OFFSET, and
 * no admin list is a million pages long.
 */
export const MAX_LIST_PAGE = 1_000_000;

/** Highest row offset a list route will read — the offset twin of {@link MAX_LIST_PAGE}. */
export const MAX_LIST_OFFSET = 100_000_000;

// NUL is the one character Postgres refuses in a text parameter (the query
// 500s). It cannot be part of anything an operator typed, so it is removed;
// every other character — LIKE metacharacters included — is kept verbatim.
const NUL_RE = /\u0000/g;
const DIGITS_RE = /^\d+$/;

/**
 * Normalize a free-text list param that is not the search term (a state code,
 * a comma list of facet values): strip NUL, trim, and read blank as absent.
 *
 * @param raw - The query-string value, or `null` when absent.
 * @returns The cleaned value, or `null` when nothing is left.
 */
export function normalizeListTextParam(raw: string | null | undefined): string | null {
  const trimmed = raw?.replace(NUL_RE, '').trim() ?? '';
  return trimmed === '' ? null : trimmed;
}

/**
 * Normalize a raw `?search=` value the way every list route must.
 *
 * Strips NUL, trims, treats whitespace-only as "no search", caps the length.
 * Escaping LIKE metacharacters is deliberately NOT here — that is the
 * repository's job, applied exactly once (`banned-patterns.md`, BUG-1419).
 *
 * @param raw - The query-string value, or `null` when absent.
 * @returns The trimmed, capped term — or `null` when there is nothing to
 *   search for (absent, empty, or whitespace-only), so the caller sends no
 *   predicate rather than `%   %`.
 */
export function normalizeListSearchTerm(raw: string | null | undefined): string | null {
  return normalizeListTextParam(raw)?.slice(0, MAX_LIST_SEARCH_LENGTH) ?? null;
}

// Date or date-time in ISO 8601 with a four-digit year — the shape the lot
// list's date filter sends (`Date#toISOString`). Anything looser (`1`, `today`)
// is a string Postgres may refuse as a timestamptz, i.e. a 500.
const ISO_INSTANT_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d{1,9})?)?(Z|[+-]\d{2}:\d{2})?)?$/;

/**
 * Parse a date-range bound (`?lastModifiedStart=`). A malformed value is no
 * bound — the filter falls back to "any time" rather than failing the list.
 *
 * @param raw - The query-string value, or `null` when absent.
 * @returns The instant as a canonical ISO string, or `null` for none.
 */
export function parseListInstant(raw: string | null | undefined): string | null {
  if (raw == null || !ISO_INSTANT_RE.test(raw)) return null;
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}

/** A plain non-negative decimal integer, or null for anything else (sign, decimal, exponent, space). */
function readWholeNumber(raw: string | null | undefined): number | null {
  if (raw == null || !DIGITS_RE.test(raw)) return null;
  const n = Number(raw);
  return Number.isSafeInteger(n) ? n : null;
}

/**
 * Parse a 1-based `?page=`. Anything that is not a whole number in
 * `1..MAX_LIST_PAGE` is malformed and falls back to page 1.
 *
 * @param raw - The query-string value, or `null` when absent.
 * @returns The page to read.
 */
export function parseListPage(raw: string | null | undefined): number {
  const n = readWholeNumber(raw);
  return n != null && n >= 1 && n <= MAX_LIST_PAGE ? n : 1;
}

/**
 * Parse a 0-based row `?offset=`. Anything that is not a whole number in
 * `0..MAX_LIST_OFFSET` is malformed and falls back to 0.
 *
 * @param raw - The query-string value, or `null` when absent.
 * @returns The offset to read from.
 */
export function parseListOffset(raw: string | null | undefined): number {
  const n = readWholeNumber(raw);
  return n != null && n <= MAX_LIST_OFFSET ? n : 0;
}

/**
 * ADR 059 — Search Spine § 2.5: the default page for every admin list is 50
 * rows. The ONE copy of that number (BL-2502). Every list's client default and
 * every route's `defaultLimit` read it, so no list can drift on its own.
 * `list-page-size.guard.test.ts` fails on a list default typed anywhere else.
 */
export const DEFAULT_LIST_PAGE_SIZE = 50;

/**
 * ADR 059 — Search Spine § 2.5 names Bidders as a list that keeps its own size, and BL-2502
 * AC 3 keeps it at 100. The tab is the registration queue staff work through
 * before a sale, approving row by row, so one page holds as much of it as the
 * route will serve: 100 is also the route's ceiling. The page and the route
 * both read this constant, so the pager never describes a page the server
 * won't return.
 */
export const AUCTION_BIDDERS_PAGE_SIZE = 100;

/** The per-route bounds for {@link parseListLimit}. */
export interface ListLimitBounds {
  /** Rows to read when the limit is absent or malformed. */
  defaultLimit: number;
  /** The route's documented maximum; a larger request is capped to it. */
  maxLimit: number;
}

/**
 * Parse a row `?limit=` (or `?pageSize=`). An over-maximum limit is capped; an
 * absent, malformed, zero or negative one falls back to the route's default —
 * never "no limit".
 *
 * @param raw - The query-string value, or `null` when absent.
 * @param bounds - The route's default and maximum.
 * @returns The number of rows to read, in `1..maxLimit`.
 */
export function parseListLimit(raw: string | null | undefined, bounds: ListLimitBounds): number {
  if (raw != null && DIGITS_RE.test(raw) && Number(raw) > bounds.maxLimit) return bounds.maxLimit;
  const n = readWholeNumber(raw);
  return n != null && n >= 1 ? n : bounds.defaultLimit;
}

/**
 * BL-1872: every facet is multi-select. BL-2430 moved this here from the server's
 * `inventory-filters.ts` so the offline Inventory query parses the URL the same way. The wire form is a comma list under the
 * facet's existing param name (`?status=draft,live`), so a single value — every
 * link and caller that predates the change — still means what it meant.
 *
 * Normalises one param to the distinct values it names: split on `,`, trim,
 * drop blanks and the legacy `all` sentinel. An empty result is `null` (no
 * filter), never `[]` — "match nothing" is not a state any control can ask for.
 *
 * @param raw - `searchParams.get(<facet>)`, verbatim.
 * @returns The distinct values, or `null` for no filter.
 */
export function normalizeInventoryFilterList(raw: string | null | undefined): string[] | null {
  // BUG-1794: NUL-stripped first — a NUL-bearing value 500s at Postgres.
  const text = normalizeListTextParam(raw);
  if (text == null) return null;
  const values = [...new Set(text.split(',').map((v) => v.trim()).filter((v) => v.length > 0 && v !== 'all'))];
  return values.length > 0 ? values : null;
}

/**
 * A facet param's distinct trimmed tokens, through the same normaliser as
 * {@link normalizeInventoryFilterList} (NUL-stripped, blanks and `all` dropped),
 * as a list: empty means "no filter". The Invoices and Settlements parsers read
 * every comma-list facet through it (BL-2516).
 *
 * @param raw - `searchParams.get(<facet>)`, verbatim.
 * @returns The distinct tokens; empty for no filter.
 */
export function parseListTokens(raw: string | null | undefined): string[] {
  return normalizeInventoryFilterList(raw) ?? [];
}

/** The Assignee facet's token for "no one assigned" (Invoices, Settlements). */
export const LIST_ASSIGNEE_UNASSIGNED = 'unassigned';

/** The Assignee facet's label for "no one assigned" (BL-2050 AC 5, BL-2516). */
export const LIST_ASSIGNEE_UNASSIGNED_LABEL = 'Unassigned';

/** A list's Created filter: a preset, and the From/To range a `custom` preset reads. */
export interface ListCreatedFilter {
  preset: ListDatePreset;
  range: LogicalDateRange;
}

/**
 * Parse a list's Created filter from its `created` and `created_range` params.
 * An unknown preset reads as `all_time` (no filter), never an error (BUG-1794).
 *
 * @param get - Reads one raw param (`URLSearchParams#get`).
 * @returns The preset and its range.
 */
export function parseListCreatedFilter(get: (key: string) => string | null): ListCreatedFilter {
  const preset = get('created');
  return {
    preset: (LIST_DATE_PRESETS as readonly string[]).includes(preset ?? '') ? (preset as ListDatePreset) : 'all_time',
    range: parseLogicalDateRange(get('created_range')),
  };
}
